import * as React from 'react';

import {SaveButton} from 'shells/button/save-button/save-button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';

import type {EnrichmentResponse} from 'nutshell-graphql-types';
import type {GetAccountEnrichment_accountEnrichment as EnrichmentField} from 'nutshell-graphql-types';

import {DataSection, ReviewAdditionalFields} from '.';

import styles from './review-enrichment-data-modal.module.css';

export type AdditionalFields = {
    socialFields: Array<EnrichmentField> | null | undefined;
    companyFields: Array<EnrichmentField> | null | undefined;
};

export type EnrichmentResponseStatus = EnrichmentResponse | 'ERROR';

type Props = {
    entityName: string | null | undefined;
    onClose: () => void;
    data: Array<EnrichmentField> | null | undefined;
    additionalFields: AdditionalFields;
    onSave: (isSocialProfilesChecked?: boolean, isRelatedCompanyChecked?: boolean) => void;
    onDecline: () => void;
    error: string | null | undefined;
    loading: boolean;
    actionStatus: EnrichmentResponseStatus | null | undefined;
    setActionStatus: (actionStatus?: EnrichmentResponseStatus | null | undefined) => void;
};

export function ReviewEnrichmentDataModal(props: Props) {
    const [isSocialProfilesChecked, setIsSocialProfilesChecked] = React.useState<boolean>(true);
    const [isRelatedCompanyChecked, setIsRelatedCompanyChecked] = React.useState<boolean>(true);

    const hasAdditionalFields =
        props.additionalFields &&
        (props.additionalFields.socialFields || props.additionalFields.companyFields);

    return (
        <Modal
            isOpen={true}
            onClose={props.onClose}
            headerText={`Review new data${props.entityName ? ` for ${props.entityName}` : ''}`}
        >
            <div className={styles['review-fields']}>
                {props.data && (
                    <div className={styles.container}>
                        <DataSection label='Current data' data={props.data} isShowingNew={false} />
                        <DataSection label='Found data' data={props.data} isShowingNew={true} />
                    </div>
                )}
                {hasAdditionalFields && (
                    <ReviewAdditionalFields
                        additionalFields={props.additionalFields}
                        isRelatedCompanyChecked={isRelatedCompanyChecked}
                        setIsRelatedCompanyChecked={setIsRelatedCompanyChecked}
                        isSocialProfilesChecked={isSocialProfilesChecked}
                        setIsSocialProfilesChecked={setIsSocialProfilesChecked}
                    />
                )}
            </div>
            <div className={styles.footer}>
                {props.error && props.actionStatus === 'ERROR' ? (
                    <FlashBanner
                        type='error'
                        hasBorder={true}
                        leftAlignContent={true}
                        onHidden={() => {
                            props.setActionStatus(undefined);
                        }}
                        // Animation causes this thing to leave what I can only describe as a "residue" behind
                        animate={false}
                    >
                        {props.error}
                    </FlashBanner>
                ) : undefined}
                <FooterBar
                    left={
                        <ButtonMover size='big' direction='left'>
                            <SaveButton
                                variant='text'
                                size='big'
                                // @ts-expect-error
                                onClick={() => {
                                    props.onDecline();
                                }}
                                isSaving={props.loading && props.actionStatus === 'DECLINED'}
                                errorSaving={
                                    Boolean(props.error) && props.actionStatus === 'DECLINED'
                                }
                                buttonText='Decline new data'
                                savingText='Declining…'
                                successText='Declined!'
                            />
                        </ButtonMover>
                    }
                    right={
                        <SaveButton
                            // @ts-expect-error
                            onClick={() => {
                                props.onSave(isSocialProfilesChecked, isRelatedCompanyChecked);
                            }}
                            isSaving={props.loading && props.actionStatus === 'ACCEPTED'}
                            errorSaving={Boolean(props.error) && props.actionStatus === 'ACCEPTED'}
                            buttonText='Accept & update'
                            savingText='Updating'
                            successText='Updated!'
                        />
                    }
                />
            </div>
        </Modal>
    );
}
